import React, { useState, useEffect } from 'react';
import { Search, AlertTriangle, CheckCircle, XCircle, Clock, Plus, Edit2, Trash2, Eye, User, FileText, Calendar } from 'lucide-react';
import { toast } from 'react-toastify';
import api from '../api';
import Pagination from '../common/Pagination';
import EmptyState from '../common/EmptyState';
import Modal from '../Modal';
import FormInput from '../FormInput';
import ConfirmationModal from '../ConfirmationModal';

const ReclamationsTab = () => {
  const [reclamations, setReclamations] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [candidats, setCandidats] = useState([]);
  const [epreuves, setEpreuves] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [pagination, setPagination] = useState(null);

  // États pour les modals
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [selectedReclamation, setSelectedReclamation] = useState(null);
  const [detailReclamation, setDetailReclamation] = useState(null);
  const [formData, setFormData] = useState({
    session_concours: '',
    candidat: '',
    epreuve: '',
    type_reclamation: '',
    description: '',
    pieces_jointes: null,
    statut: 'SOUMISE',
    reponse: ''
  });
  const [formErrors, setFormErrors] = useState({});

  // États pour la confirmation de suppression
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [reclamationToDelete, setReclamationToDelete] = useState(null);
  const [confirmConfig, setConfirmConfig] = useState({});

  const fetchReclamations = async () => {
    setLoading(true);
    setError('');
    
    try {
      const queryParams = new URLSearchParams({
        page: currentPage,
        page_size: pageSize
      });
      
      if (searchTerm.trim()) {
        queryParams.append('search', searchTerm.trim());
      }
      
      const response = await api.get(`/concours/reclamations/?${queryParams}`);
      
      if (response.data) {
        const responseData = response.data.data || response.data.results || [];
        setReclamations(Array.isArray(responseData) ? responseData : []);
        setPagination(response.data.pagination || {
          count: response.data.count || 0,
          total_pages: Math.ceil((response.data.count || 0) / pageSize),
          current_page: currentPage,
          page_size: pageSize
        });
      }
    } catch (error) {
      console.error('Erreur lors du chargement:', error);
      setError('Erreur lors du chargement des réclamations');
      setReclamations([]);
      setPagination(null);
      toast.error('Impossible de charger les réclamations');
    } finally {
      setLoading(false);
    }
  };

  const fetchSessions = async () => {
    try {
      const response = await api.get('/concours/sessions/');
      const responseData = response.data.data || response.data.results || [];
      setSessions(Array.isArray(responseData) ? responseData : []);
    } catch (error) {
      console.error('Erreur lors du chargement des sessions:', error);
      setSessions([]);
    }
  };

  const fetchCandidatsBySession = async (sessionId) => {
    try {
      const response = await api.get(`/concours/candidats/?session=${sessionId}`);
      const responseData = response.data.data || response.data.results || [];
      setCandidats(Array.isArray(responseData) ? responseData : []);
    } catch (error) {
      console.error('Erreur lors du chargement des candidats:', error);
      setCandidats([]);
    }
  };

  const fetchEpreuvesBySession = async (sessionId) => {
    try {
      const response = await api.get(`/concours/sessions/${sessionId}/epreuves/`);
      const responseData = response.data.data || response.data.results || [];
      setEpreuves(Array.isArray(responseData) ? responseData : []);
    } catch (error) {
      console.error('Erreur lors du chargement des épreuves:', error);
      setEpreuves([]);
    }
  };

  useEffect(() => {
    fetchSessions();
  }, []);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchReclamations();
    }, searchTerm ? 300 : 0);

    return () => clearTimeout(timeoutId);
  }, [currentPage, pageSize, searchTerm]);

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setCurrentPage(1);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  // Gestion du formulaire
  const handleInputChange = (e) => {
    const { name, value, files } = e.target;
    
    if (name === 'pieces_jointes') {
      setFormData(prev => ({
        ...prev,
        [name]: files[0] || null
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));

      // Si on change la session, charger ses candidats et épreuves
      if (name === 'session_concours' && value) {
        fetchCandidatsBySession(value);
        fetchEpreuvesBySession(value);
        // Reset les champs candidat et épreuve
        setFormData(prev => ({
          ...prev,
          candidat: '',
          epreuve: ''
        }));
      }
    }

    // Clear l'erreur si l'utilisateur corrige le champ
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Ouvrir le modal de création/modification
  const handleOpenModal = (reclamation = null) => {
    if (reclamation) {
      setSelectedReclamation(reclamation);
      const sessionId = reclamation.epreuve?.session_concours?.id;
      
      setFormData({
        session_concours: sessionId || '',
        candidat: reclamation.candidat?.id || '',
        epreuve: reclamation.epreuve?.id || '',
        type_reclamation: reclamation.type_reclamation || '',
        description: reclamation.description || '',
        pieces_jointes: null, // Ne pas pré-remplir le fichier
        statut: reclamation.statut || 'SOUMISE',
        reponse: reclamation.reponse || ''
      });

      // Charger les candidats et épreuves de la session
      if (sessionId) {
        fetchCandidatsBySession(sessionId);
        fetchEpreuvesBySession(sessionId);
      }
    } else {
      setSelectedReclamation(null);
      setFormData({
        session_concours: '',
        candidat: '',
        epreuve: '',
        type_reclamation: '',
        description: '',
        pieces_jointes: null,
        statut: 'SOUMISE',
        reponse: ''
      });
      // Reset les listes
      setCandidats([]);
      setEpreuves([]);
    }
    setFormErrors({});
    setIsModalOpen(true);
  };

  // Ouvrir le modal de détails
  const handleOpenDetailModal = (reclamation) => {
    setDetailReclamation(reclamation);
    setIsDetailModalOpen(true);
  };

  // Validation du formulaire
  const validateForm = () => {
    const errors = {};
    
    if (!formData.session_concours) {
      errors.session_concours = "La session de concours est requise";
    }
    if (!formData.candidat) {
      errors.candidat = "Le candidat est requis";
    }
    if (!formData.epreuve) {
      errors.epreuve = "L'épreuve est requise";
    }
    if (!formData.type_reclamation.trim()) {
      errors.type_reclamation = "Le type de réclamation est requis";
    }
    if (!formData.description.trim()) {
      errors.description = "La description est requise";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Soumettre le formulaire
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setSubmitting(true);
    try {
      const formDataToSend = new FormData();
      
      Object.keys(formData).forEach(key => {
        if (key === 'pieces_jointes' && formData[key]) {
          formDataToSend.append(key, formData[key]);
        } else if (key !== 'pieces_jointes' && formData[key] !== null) {
          formDataToSend.append(key, formData[key]);
        }
      });

      if (selectedReclamation) {
        // Mise à jour
        await api.put(`/concours/reclamations/${selectedReclamation.id}/`, formDataToSend, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        toast.success("Réclamation mise à jour avec succès");
      } else {
        // Création
        await api.post('/concours/reclamations/', formDataToSend, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        toast.success("Réclamation créée avec succès");
      }

      fetchReclamations();
      setIsModalOpen(false);
    } catch (error) {
      console.error("Erreur lors de l'opération:", error);
      
      let errorMsg = "Une erreur est survenue. Veuillez réessayer.";
      if (error.response && error.response.data) {
        const responseData = error.response.data;
        if (typeof responseData === 'object') {
          const errorMessages = Object.entries(responseData)
            .map(([key, value]) => `${key}: ${Array.isArray(value) ? value.join(', ') : value}`)
            .join('\n');
          errorMsg = errorMessages || errorMsg;
        } else if (typeof responseData === 'string') {
          errorMsg = responseData;
        }
      }
      toast.error(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  // Gestion de la suppression
  const handleOpenDeleteModal = (reclamation) => {
    setReclamationToDelete(reclamation);
    setConfirmConfig({
      title: "Supprimer la réclamation",
      message: `Êtes-vous sûr de vouloir supprimer cette réclamation ?`,
      confirmText: "Supprimer",
      type: "danger"
    });
    setIsConfirmModalOpen(true);
  };

  const handleDelete = async () => {
    if (!reclamationToDelete) return;
    
    setDeleteLoading(true);
    try {
      await api.delete(`/concours/reclamations/${reclamationToDelete.id}/`);
      toast.success("Réclamation supprimée avec succès");
      fetchReclamations();
    } catch (error) {
      console.error("Erreur lors de la suppression:", error);
      toast.error("Impossible de supprimer cette réclamation. Veuillez réessayer.");
    } finally {
      setDeleteLoading(false);
      setIsConfirmModalOpen(false);
    }
  };

  const getStatusBadge = (statut) => {
    const statusConfig = {
      'SOUMISE': { color: 'bg-blue-100 text-blue-800', icon: Clock, label: 'Soumise' },
      'EN_COURS': { color: 'bg-yellow-100 text-yellow-800', icon: Clock, label: 'En cours' },
      'ACCEPTEE': { color: 'bg-green-100 text-green-800', icon: CheckCircle, label: 'Acceptée' },
      'REJETEE': { color: 'bg-red-100 text-red-800', icon: XCircle, label: 'Rejetée' }
    };
    
    const config = statusConfig[statut] || { color: 'bg-gray-100 text-gray-800', icon: Clock, label: statut };
    const Icon = config.icon;
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        <Icon className="h-3 w-3 mr-1" />
        {config.label}
      </span>
    );
  };

  const getTypeDisplay = (type) => {
    const typeConfig = {
      'NOTE': 'Contestation de note',
      'ABSENCE': "Justification d'absence",
      'TECHNIQUE': 'Problème technique',
      'AUTRE': 'Autre'
    };
    return typeConfig[type] || type;
  };

  // Options pour les types de réclamation
  const typeOptions = [
    { value: '', label: 'Sélectionner un type' },
    { value: 'NOTE', label: 'Contestation de note' },
    { value: 'ABSENCE', label: "Justification d'absence" },
    { value: 'TECHNIQUE', label: 'Problème technique' },
    { value: 'AUTRE', label: 'Autre' }
  ];

  // Options pour les statuts
  const statutOptions = [
    { value: 'SOUMISE', label: 'Soumise' },
    { value: 'EN_COURS', label: 'En cours' },
    { value: 'ACCEPTEE', label: 'Acceptée' },
    { value: 'REJETEE', label: 'Rejetée' }
  ];

  // Rendering du footer du modal de création/modification
  const renderModalFooter = () => (
    <div className="flex justify-end space-x-4">
      <button
        type="button"
        className="inline-flex justify-center px-6 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        onClick={() => setIsModalOpen(false)}
      >
        Annuler
      </button>
      <button
        type="button"
        className={`inline-flex justify-center px-6 py-2.5 text-sm font-medium text-white bg-[#202654] border border-transparent rounded-md shadow-sm hover:bg-[#161c3f] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#202654] ${submitting ? 'opacity-75 cursor-not-allowed' : ''}`}
        onClick={handleSubmit}
        disabled={submitting}
      >
        {submitting ? (
          <>
            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Traitement...
          </>
        ) : (
          selectedReclamation ? 'Mettre à jour' : 'Créer'
        )}
      </button>
    </div>
  );

  // Rendering du footer du modal de détails
  const renderDetailModalFooter = () => (
    <div className="flex justify-end">
      <button
        type="button"
        className="inline-flex justify-center px-6 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        onClick={() => setIsDetailModalOpen(false)}
      >
        Fermer
      </button>
    </div>
  );

  if (loading && reclamations.length === 0) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
          <input
            type="text"
            placeholder="Rechercher une réclamation..."
            value={searchTerm}
            onChange={handleSearchChange}
            className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <button
          onClick={() => handleOpenModal()}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-[#202654] hover:bg-[#202654]/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#202654]"
        >
          <Plus className="h-5 w-5 mr-2" />
          Nouvelle Réclamation
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {reclamations.length === 0 && !loading ? (
        <EmptyState
          icon={AlertTriangle}
          title="Aucune réclamation"
          description="Aucune réclamation trouvée."
          actionLabel="Créer une réclamation"
          onAction={() => handleOpenModal()}
        />
      ) : (
        <div className="bg-white shadow-sm rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Candidat
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type & Épreuve
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Statut
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {reclamations.map((reclamation) => (
                  <tr key={reclamation.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          <div className="h-10 w-10 rounded-lg bg-red-100 flex items-center justify-center">
                            <AlertTriangle className="h-5 w-5 text-red-600" />
                          </div>
                        </div>
                        <div className="ml-4 min-w-0 flex-1">
                          <div className="text-sm font-medium text-gray-900">
                            {reclamation.candidat?.nom_complet || 'Candidat inconnu'}
                          </div>
                          <div className="text-sm text-gray-500">
                            {reclamation.candidat?.numero_candidat || 'N/A'}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="space-y-1">
                        <div className="text-sm font-medium text-gray-900">
                          {getTypeDisplay(reclamation.type_reclamation)}
                        </div>
                        <div className="text-sm text-gray-500">
                          {reclamation.epreuve?.matiere?.nom || 'Épreuve non spécifiée'}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center text-sm text-gray-900">
                        <Calendar className="h-4 w-4 mr-1 text-gray-400" />
                        {reclamation.date_soumission ? 
                          new Date(reclamation.date_soumission).toLocaleDateString('fr-FR') : 
                          'Non définie'
                        }
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(reclamation.statut)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end space-x-2">
                        <button
                          onClick={() => handleOpenDetailModal(reclamation)}
                          className="text-blue-600 hover:text-blue-900 p-1 rounded-full hover:bg-blue-50 transition-colors"
                          title="Voir les détails"
                        >
                          <Eye className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => handleOpenModal(reclamation)}
                          className="text-indigo-600 hover:text-indigo-900 p-1 rounded-full hover:bg-indigo-50 transition-colors"
                          title="Modifier"
                        >
                          <Edit2 className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => handleOpenDeleteModal(reclamation)}
                          className="text-red-600 hover:text-red-900 p-1 rounded-full hover:bg-red-50 transition-colors"
                          title="Supprimer"
                        >
                          <Trash2 className="h-5 w-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {pagination && pagination.total_pages > 1 && (
            <div className="bg-white px-4 py-3 border-t border-gray-200">
              <Pagination
                currentPage={currentPage}
                totalPages={pagination.total_pages}
                pageSize={pageSize}
                totalCount={pagination.count}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
              />
            </div>
          )}
        </div>
      )}

      {/* Modal de création/modification */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={selectedReclamation ? 'Modifier la réclamation' : 'Nouvelle réclamation'}
        footer={renderModalFooter()}
        maxWidth="max-w-2xl"
      >
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="bg-blue-50 p-4 rounded-lg">
            <h4 className="text-sm font-medium text-blue-900 mb-3">Sélection de la session</h4>
            <FormInput
              label="Session de concours"
              type="select"
              name="session_concours"
              value={formData.session_concours}
              onChange={handleInputChange}
              options={[
                { value: '', label: 'Sélectionner une session' },
                ...sessions.map(session => ({ 
                  value: session.id, 
                  label: `${session.nom} - ${session.type_concours?.nom || ''}` 
                }))
              ]}
              required
              error={formErrors.session_concours}
              icon={Calendar}
            />
          </div>

          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="text-sm font-medium text-gray-900 mb-3">Informations de la réclamation</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormInput
                label="Candidat"
                type="select"
                name="candidat"
                value={formData.candidat}
                onChange={handleInputChange}
                options={[
                  { value: '', label: formData.session_concours ? 'Sélectionner un candidat' : 'Sélectionnez d\'abord une session' },
                  ...candidats.map(candidat => ({ 
                    value: candidat.id, 
                    label: `${candidat.nom_complet} - ${candidat.numero_candidat}` 
                  }))
                ]}
                required
                error={formErrors.candidat}
                icon={User}
                disabled={!formData.session_concours}
              />
              
              <FormInput
                label="Épreuve"
                type="select"
                name="epreuve"
                value={formData.epreuve}
                onChange={handleInputChange}
                options={[
                  { value: '', label: formData.session_concours ? 'Sélectionner une épreuve' : 'Sélectionnez d\'abord une session' },
                  ...epreuves.map(epreuve => ({ 
                    value: epreuve.id, 
                    label: epreuve.matiere?.nom || `Épreuve ${epreuve.id}`
                  }))
                ]}
                required
                error={formErrors.epreuve}
                disabled={!formData.session_concours}
              />

              <FormInput
                label="Type de réclamation"
                type="select"
                name="type_reclamation"
                value={formData.type_reclamation}
                onChange={handleInputChange}
                options={typeOptions}
                required
                error={formErrors.type_reclamation}
                icon={AlertTriangle}
              />

              {selectedReclamation && (
                <FormInput
                  label="Statut"
                  type="select"
                  name="statut"
                  value={formData.statut}
                  onChange={handleInputChange}
                  options={statutOptions}
                  error={formErrors.statut}
                />
              )}
            </div>
          </div>

          <div className="bg-yellow-50 p-4 rounded-lg">
            <h4 className="text-sm font-medium text-yellow-900 mb-3">Description</h4>
            <FormInput
              label="Description de la réclamation"
              type="textarea"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              placeholder="Décrivez votre réclamation en détail..."
              required
              error={formErrors.description}
              rows={4}
              icon={FileText}
            />
          </div>

          <div className="bg-orange-50 p-4 rounded-lg">
            <h4 className="text-sm font-medium text-orange-900 mb-3">Pièces jointes</h4>
            <FormInput
              label="Fichier justificatif (optionnel)"
              type="file"
              name="pieces_jointes"
              onChange={handleInputChange}
              accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
              error={formErrors.pieces_jointes}
              help="Formats acceptés: PDF, images, documents Word (max 5MB)"
            />
          </div>

          {selectedReclamation && (
            <div className="bg-green-50 p-4 rounded-lg">
              <h4 className="text-sm font-medium text-green-900 mb-3">Réponse administrative</h4>
              <FormInput
                label="Réponse à la réclamation"
                type="textarea"
                name="reponse"
                value={formData.reponse}
                onChange={handleInputChange}
                placeholder="Réponse de l'administration..."
                error={formErrors.reponse}
                rows={3}
              />
            </div>
          )}

          <div className="text-sm text-gray-500 mt-4">
            <span className="text-red-500">*</span> Champs obligatoires
          </div>
        </form>
      </Modal>

      {/* Modal de détails */}
      <Modal
        isOpen={isDetailModalOpen}
        onClose={() => setIsDetailModalOpen(false)}
        title="Détails de la réclamation"
        footer={renderDetailModalFooter()}
        maxWidth="max-w-3xl"
      >
        {detailReclamation && (
          <div className="space-y-6">
            {/* Informations générales */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <AlertTriangle className="h-5 w-5 mr-2 text-red-600" />
                Informations générales
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Candidat</label>
                  <p className="mt-1 text-sm text-gray-900">
                    {detailReclamation.candidat?.nom_complet || 'Non spécifié'}
                  </p>
                  <p className="text-xs text-gray-500">
                    {detailReclamation.candidat?.numero_candidat || 'N/A'}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Type de réclamation</label>
                  <p className="mt-1 text-sm text-gray-900">
                    {getTypeDisplay(detailReclamation.type_reclamation)}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Épreuve concernée</label>
                  <p className="mt-1 text-sm text-gray-900">
                    {detailReclamation.epreuve?.matiere?.nom || 'Non spécifiée'}
                  </p>
                  <p className="text-xs text-gray-500">
                    {detailReclamation.epreuve?.session_concours?.nom || ''}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Statut</label>
                  <div className="mt-1">{getStatusBadge(detailReclamation.statut)}</div>
                </div>
              </div>
            </div>

            {/* Description */}
            <div className="bg-blue-50 p-4 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <FileText className="h-5 w-5 mr-2 text-blue-600" />
                Description
              </h3>
              <p className="text-sm text-gray-900 whitespace-pre-wrap">
                {detailReclamation.description || 'Aucune description fournie'}
              </p>
            </div>

            {/* Pièces jointes */}
            {detailReclamation.pieces_jointes && (
              <div className="bg-yellow-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Pièces jointes</h3>
                <a
                  href={detailReclamation.pieces_jointes}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center text-blue-600 hover:text-blue-800"
                >
                  <FileText className="h-4 w-4 mr-1" />
                  Voir le fichier joint
                </a>
              </div>
            )}

            {/* Réponse */}
            {detailReclamation.reponse && (
              <div className="bg-green-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Réponse administrative</h3>
                <p className="text-sm text-gray-900 whitespace-pre-wrap">
                  {detailReclamation.reponse}
                </p>
                {detailReclamation.traite_par && (
                  <div className="mt-2 text-xs text-gray-500">
                    Traitée par: {detailReclamation.traite_par.first_name} {detailReclamation.traite_par.last_name}
                  </div>
                )}
              </div>
            )}

            {/* Dates */}
            <div className="border-t pt-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-500">
                <div>
                  <label className="block font-medium">Date de soumission</label>
                  <p>
                    {detailReclamation.date_soumission ? 
                      new Date(detailReclamation.date_soumission).toLocaleString('fr-FR') : 
                      'Non disponible'
                    }
                  </p>
                </div>
                <div>
                  <label className="block font-medium">Date de traitement</label>
                  <p>
                    {detailReclamation.date_traitement ? 
                      new Date(detailReclamation.date_traitement).toLocaleString('fr-FR') : 
                      'Non traitée'
                    }
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* Modal de confirmation de suppression */}
      <ConfirmationModal 
        isOpen={isConfirmModalOpen}
        onClose={() => setIsConfirmModalOpen(false)}
        onConfirm={handleDelete}
        title={confirmConfig.title || "Supprimer la réclamation"}
        message={confirmConfig.message || "Êtes-vous sûr de vouloir supprimer cette réclamation ?"}
        confirmText={confirmConfig.confirmText || "Supprimer"}
        cancelText="Annuler"
        type={confirmConfig.type || "danger"}
        isLoading={deleteLoading}
      />
    </div>
  );
};

export default ReclamationsTab; 